// Schemes typed by hand rarely come out clean: "http//", "https:/", "HTTP://"
// and even "http:://" all show up on the store url field.
const SCHEME_TYPO = /^(https?)[\s:;/]*/i;
// A scheme has no dots, so 'loja.com.br:8080' is read as a host with a port.
const SCHEME = /^[a-z][a-z0-9+-]*:/i;
// A host we can accept has at least one dot and a plausible tld, which rules
// out "loja", "localhost" and bare ip addresses.
const HOSTNAME = /^([a-z0-9](-*[a-z0-9])*\.)+[a-z]{2,}$/i;

/**
 * Understands the ways people usually type the url of their store
 * (loja.com.br, www.loja.com.br, http//loja.com.br) and returns it in a
 * standard form, or null when there is no url to be understood.
 *
 * The result is always https: a store that still answers on http would be
 * saved as an insecure link inside the app, and every platform we integrate
 * with serves the storefront over https anyway. The path is kept, since some
 * platforms host the store on a subfolder, but the query and the hash are
 * dropped and so is the trailing slash, which would otherwise turn into
 * '//conta' when storeAccountUrl and storeCartUrl are built from it.
 */
export function normalizeUrl(input) {
    if (typeof input !== 'string') return null;

    // Text pasted from WhatsApp or from an email tends to bring spaces,
    // zero-width characters and quotes along with the url.
    let value = input.trim().replace(/[\s\u200B-\u200D\uFEFF"'<>]/g, '');
    if (!value) return null;

    const typo = value.match(SCHEME_TYPO);

    if (typo) {
        value = `https://${value.slice(typo[0].length)}`;
    } else if (SCHEME.test(value)) {
        // Anything that is not http(s) is not a store url (ftp:, javascript:, ...)
        return null;
    } else {
        value = `https://${value}`;
    }

    let url;

    try {
        url = new URL(value);
    } catch {
        return null;
    }

    if (!HOSTNAME.test(url.hostname)) return null;

    const port = url.port ? `:${url.port}` : '';
    const path = url.pathname.replace(/\/+$/, '');

    return `https://${url.hostname}${port}${path}`;
}

/**
 * Whether the value can be understood as a url. Meant for the `validate` rule
 * of a form field, whose value is normalized on blur.
 */
export function isValidUrl(value) {
    return normalizeUrl(value) !== null;
}

/**
 * Wraps the object returned by react-hook-form's `register` so that leaving the
 * field replaces what was typed with its standard form, keeping the validation
 * the register already does. For fields bound with `Controller`, normalize on
 * the `onBlur` of the field itself instead.
 */
export function normalizeUrlOnBlur(field, setValue) {
    return {
        ...field,
        onBlur: async (event) => {
            await field.onBlur(event);

            const normalized = normalizeUrl(event.target.value);

            if (normalized && normalized !== event.target.value) {
                setValue(field.name, normalized, { shouldValidate: true, shouldDirty: true });
            }
        }
    };
}
